import {
  DadoSubscription,
  SubscriptionProvider
} from '@dadohr/shared/domainTypes/subscription';
import { ButtonProps } from '@material-ui/core';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import ENV from '../../../../env.json';
import { useUnsafeTranslation } from '../../../../i18next';
import { callFirebaseFunction } from '../../../../services/firebaseFunctions';

const stripePromise = loadStripe(ENV.stripe.publishableKey);

export const StripeProvider: React.FC = ({ children }) => {
  return <Elements stripe={stripePromise}>{children}</Elements>;
};

const createCheckoutSession = (
  orgId: string,
  priceId: string,
  currency: string
) => {
  return callFirebaseFunction<{ sessionId: string }>(
    'subscription_createCheckoutSession',
    { orgId, priceId, currency }
  );
};

const createCustomerPortlaSession = (orgId: string) => {
  return callFirebaseFunction<{ sessionUrl: string }>(
    'subscription_createCustomerPortalSession',
    { orgId }
  );
};

export const StripeCheckoutButton = ({
  orgId,
  children,
  priceId,
  currency,
  ...props
}: { orgId: string; priceId: string; currency: string } & Omit<
  ButtonProps,
  'onClick'
>) => {
  const { t } = useUnsafeTranslation();
  const stripe = useStripe();
  const checkout = async () => {
    const { sessionId } = await createCheckoutSession(orgId, priceId, currency);
    stripe?.redirectToCheckout({ sessionId });
  };

  return (
    <ButtonWithPromise
      pending={'Redirecting...'}
      variant="contained"
      color="primary"
      {...props}
      onClick={checkout}
    >
      {children || t('Go to Checkout')}
    </ButtonWithPromise>
  );
};

export const StripeCustomerPortalButton = ({
  orgId,
  subscription,
  children,
  ...props
}: { orgId: string; subscription: DadoSubscription | null } & Omit<
  ButtonProps,
  'onClick'
>) => {
  const { t } = useUnsafeTranslation();
  const openPortal = async () => {
    const { sessionUrl } = await createCustomerPortlaSession(orgId);
    window.open(sessionUrl);
  };

  if (subscription?.provider !== SubscriptionProvider.STRIPE) {
    return null;
  }

  return (
    <ButtonWithPromise
      pending={'Redirecting...'}
      variant="contained"
      color="primary"
      {...props}
      onClick={openPortal}
    >
      {children || t('Manage subscription')}
    </ButtonWithPromise>
  );
};
